import React from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { WaveDivider } from "@/components/decor";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import YouthSizeGuide from "@/components/YouthSizeGuide";
import CapSizeGuide from "@/components/CapSizeGuide";

const TABLES = {
  Unisex: { cols: ["Size", "Chest (cm)", "Waist (cm)"], rows: [["XS", "86–91", "71–76"], ["S", "92–97", "77–82"], ["M", "98–104", "83–89"], ["L", "105–112", "90–97"], ["XL", "113–121", "98–106"], ["2XL", "122–130", "107–115"]] },
  Kids: null,
  Caps: null,
  Dogs: { cols: ["Size", "Neck (cm)", "Chest (cm)", "Back Length (cm)"], rows: [["XS", "20–25", "30–40", "20–25"], ["S", "25–32", "40–50", "25–32"], ["M", "32–40", "50–62", "32–40"], ["L", "40–48", "62–74", "40–48"]] },
};

export default function SizeGuide() {
  useSeo("Size Guide", "Measurement charts for unisex adult, kids', caps and dog products.");
  const [params] = useSearchParams();
  const want = (params.get("tab") || "").toLowerCase();
  const initial = Object.keys(TABLES).find((k) => k.toLowerCase() === ({ women: "unisex", men: "unisex", cap: "caps", hats: "caps" }[want] || want)) || "Unisex";
  return (
    <div>
      <section className="relative bg-[#7ED957]/20 py-14 text-center overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <span className="font-hand text-2xl text-[#FF6B9D]">Find your perfect fit</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A] mt-1">Size Guide</h1>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Tabs defaultValue={initial}>
          <TabsList className="bg-white rounded-full p-1 soft-shadow">
            {Object.keys(TABLES).map((k) => (
              <TabsTrigger key={k} value={k} className="rounded-full data-[state=active]:bg-[#FFD93D] font-display" data-testid={`size-tab-${k.toLowerCase()}`}>{k}</TabsTrigger>
            ))}
          </TabsList>
          {Object.entries(TABLES).map(([k, t]) => (
            <TabsContent key={k} value={k} className="mt-6">
              {k === "Kids" ? <YouthSizeGuide /> : k === "Caps" ? <CapSizeGuide /> : (
              <div className="bg-white rounded-3xl soft-shadow overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-[#FFD93D]/30"><tr>{t.cols.map((c) => <th key={c} className="px-4 py-3 font-display font-bold text-[#19324A] text-sm">{c}</th>)}</tr></thead>
                  <tbody>{t.rows.map((r, i) => <tr key={i} className="border-t border-[#19324A]/8">{r.map((cell, j) => <td key={j} className="px-4 py-3 text-[#19324A]/80 text-sm">{cell}</td>)}</tr>)}</tbody>
                </table>
              </div>
              )}
              {k === "Dogs" && (
                <div className="mt-5 bg-[#4CC9F0]/15 rounded-3xl p-5 text-[#19324A]/80">
                  <p className="font-display font-bold text-[#19324A] mb-2">How to measure your dog 🐾</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Neck:</strong> around the base of the neck where a collar sits.</li>
                    <li><strong>Chest:</strong> around the widest part, just behind the front legs.</li>
                    <li><strong>Back Length:</strong> from the base of the neck to the base of the tail.</li>
                  </ul>
                  <p className="text-sm mt-3">Please measure your dog rather than relying solely on breed — every Bulldog is beautifully unique!</p>
                  <a href="/bulldog-guide" className="btn-pink !py-2 !px-4 text-sm inline-flex items-center gap-2 mt-4" data-testid="size-guide-free-guide">Free printable Bulldog Size Guide & Care Checklist →</a>
                </div>
              )}
              <p className="text-xs text-[#19324A]/50 mt-4">Measurements are a guide. Product-specific sizing may vary — check each product page.{k !== "Kids" && k !== "Caps" && " Final charts to be confirmed by owner."}</p>
            </TabsContent>
          ))}
        </Tabs>
      </section>
    </div>
  );
}
